/**
 * Created by Yoyo Fang on 01/13/2020.
 * Description:
 *  implement interface of dashboard tiles for tam dashboard editor tile
 * ------ maintenance history ------
 * Updated by Daniel on 8/25/2021: Changed the logic to avoid the alert dialog being popped all the time in edit mode
 */

import { TamDashboardBaseConfiguration } from '../tam-dashboard-tile.base.config';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TamDashboardActions, TileMode, TamDashboardEvents } from '../tam-dashboard.model';
import { Store } from '@ngrx/store';
import { DashboardActionTypes } from '../../../redux/actions/dashboard.actions';
import { Base64Service } from '../../../tamalelibs/services/base64.service';
import { DashboardTileService } from '../dashboard-tile.service';

export class TamDashboardRichTextTileConfiguration implements TamDashboardBaseConfiguration {
    id: string;
    readonly: boolean;
    name: string;
    actionSubject$: Subject<any>;
    feedbackSubject$?: Subject<any>;
    initialized: boolean;
    data: any = { html: null };
    subscriptions: Subscription[] = [];

    private _widget: any = null;

    constructor(
        private _base64: Base64Service,
        private _store?: Store<any>,
        private _dashboardTileService?: DashboardTileService,
    ) {
        this.initialized = false;
    }

    destroy() {
        this.subscriptions.forEach((subscription: Subscription) => {
            if (subscription && !subscription.closed) {
                subscription.unsubscribe();
            }
        });
    }

    init(id: string, readonly: boolean, name: string, actionSubject$: Subject<any>,
        feedbackSubject$: Subject<any>): boolean {
        if (this.initialized === false) {
            this.id = id;
            this.readonly = readonly;
            this.name = name;
            this.actionSubject$ = actionSubject$;
            this.feedbackSubject$ = feedbackSubject$;
            const actionSubscription = this.actionSubject$.pipe(
                filter((action: any) => action && (action.id === this.id) || (action.type === TamDashboardActions.SET_TILE_ACTIVE))
            ).subscribe((action) => this._actionHandler(action));
            this.subscriptions.push(actionSubscription);
            this._loadData(true);
            this.initialized = true;
            return true;
        } else {
            return false;
        }
    }

    setWidget(widget: any) {
        this._widget = widget;
    }

    onSeriesClick = (event: any) => {
    };

    onChange(event) {
        // to mark tile dirty and force tile-setting-wrapper dirty check to be dirty
        this._handleHtmlUndefined();
        // this._widget.tile.visualization.dirty = (this.data.html !== atob(this._widget.tile.visualization.settings.html));
        if (this.data.html !== atob(this._widget.tile.visualization.settings.html)) {
            this.feedbackSubject$.next({
                id: this.id,
                type: TamDashboardEvents.SET_VALUE,
                payload: this._base64.encode(this.data.html)
            });
        }
    }

    private _returnSettings() {
        this._handleHtmlUndefined();
        this._widget.tile.visualization.settings.html = this._base64.encode(this.data.html);
        this.feedbackSubject$.next({
            type: TamDashboardEvents.TILE_SETTINGS_LOADED,
            id: this.id,
            payload: this._widget.tile,
            refreshTile: true
        });
    }

    setTileData(data) {
        this.actionSubject$.next({ id: this.id, type: TamDashboardActions.SET_TILE_DATA, payload: data });
    }

    private _actionHandler(action: any) {
        switch (action.type) {
            case TamDashboardActions.LOAD_DATA: {
                if (action.payload) {
                    this._loadData(action.payload.saveStore);
                }
                break;
            }
            case TamDashboardActions.SET_TILE_ACTIVE: {
                this.actionSubject$.next({
                    id: this.id,
                    type: TamDashboardActions.CHANGE_MODE,
                    payload: (action.id === this.id && this.readonly === false) ? TileMode.WRITE : TileMode.READ
                });
                break;
            }
            case TamDashboardActions.PULL_TILE_SETTINGS: {
                this._returnSettings();
                break;
            }
        }
    }

    private _loadData(saveStore = false) {
        if (this._dashboardTileService) {
            this._dashboardTileService.richTextLoadData(this._widget).subscribe((data) => {
                if (!data) {
                    return;
                }
                if (data.html) {
                    data.html = this._base64.decode(data.html);
                }
                this._handleHtmlUndefined();
                this.data = data;
                this.setTileData(this.data);
                this._saveDataStore(saveStore);
            });
        }
    }

    private _saveDataStore(saveStore = false) {
        if (saveStore && this._store) {
            this._store.dispatch({
                type: DashboardActionTypes.SAVE_TILE_DATA,
                payload: {
                    id: this.id,
                    data: this.data
                }
            });
        }
    }

    private _handleHtmlUndefined() {
        if (!this._widget.tile.visualization.settings) {
            this._widget.tile.visualization.settings = {};
        }
        if (!this._widget.tile.visualization.settings.html) {
            this._widget.tile.visualization.settings.html = '';
        }
    }

}
